<template>
  <b-card class="no-border animated">
    <div class="item-header clearfix mb-3">
      <h5 class="pull-left mb-0 mt-2">{{ header }}</h5>
    </div>
    <b-form @submit.prevent="onSubmit()">
      <legend>Account</legend>
      <div class="form-row">
        <div class="form-group col-md-12">
          <label for="input_fullname">Name</label>
          <input
            id="input_fullname"
            name="full name"
            type="text"
            class="form-control"
            v-model.trim="form.full_name"
            v-validate="'required'"
            :class="{'is-invalid': errors.has('full name')}"
            placeholder="Enter full name">
          <b-form-feedback>{{ errors.first('full name') }}</b-form-feedback>
        </div>
      </div>
      <div class="form-row">
        <div class="form-group col-md-6">
          <label for="input_username">User name</label>
          <input
            name="username"
            type="text"
            class="form-control"
            id="input_username"
            placeholder="Username"
            v-validate="'required|min:3|max:30'"
            :class="{'is-invalid': errors.has('username')}"
            v-model.trim="form.username"
            @change="onUsernameChange">
          <b-form-feedback>{{ errors.first('username') }}</b-form-feedback>
          <div :class="isDuplicateUsername ? 'unavailable' : 'available'">Username is unavailable.</div>
        </div>
        <div class="form-group col-md-6">
          <label for="input_email">Email</label>
          <input
            name="email"
            type="email"
            class="form-control"
            id="input_email"
            v-model.trim="form.email"
            placeholder="Email"
            v-validate="'required|email'"
            :class="{'is-invalid': errors.has('email')}">
          <b-form-feedback>{{ errors.first('email') }}</b-form-feedback>
        </div>
      </div>
      <legend v-if="!isEdit">Password</legend>
      <div
        v-if="!isEdit"
        class="form-row">
        <div class="form-group col-md-6">
          <label for="input_password">Password</label>
          <input
            id="input_password"
            name="password"
            type="password"
            v-model.lazy="form.password"
            class="form-control"
            :class="{'is-invalid': errors.has('password')}"
            v-validate="'required'"
            placeholder="Password">
          <b-form-feedback>{{ errors.first('password') }}</b-form-feedback>
        </div>
        <div class="form-group col-md-6">
          <label for="input_password2">Password confirmation</label>
          <input
            name="confirm password"
            type="password"
            v-model.lazy="form.password2"
            :class="{'is-invalid': errors.has('confirm password')}"
            class="form-control"
            id="input_password2"
            v-validate="'confirmed:password'"
            placeholder="Password confirmation">
          <b-form-feedback>{{ errors.first('confirm password') }}</b-form-feedback>
        </div>
      </div>
      <div class="form-row">
        <div class="col-md-12">
          <legend>Access</legend>
          <div
            class="form-row"
            :class="{'is-invalid': errors.has('user type'), 'no-border': !errors.has('user type')}">
            <div class="col-sm-2">
              <legend>Access level</legend>
            </div>
            <div class="col-sm-10">
              <div class="form-check" v-if="userData.type === 'SYSTEM_ADMIN'">
                <label class="form-check-label">
                  <input
                    class="form-check-input"
                    v-model="form.type"
                    type="radio"
                    name="user type"
                    v-validate="'required'"
                    value="SYSTEM_ADMIN"
                    checked>
                  <span :class="{'font-weight-bold': isSystemAdmin}">System Admin</span>
                  <small class="form-text text-muted">
                    System admin can do anything. They control everything.
                  </small>
                </label>
              </div>
              <div class="form-check" v-if="userData.type === 'SYSTEM_ADMIN'">
                <label class="form-check-label">
                  <input
                    class="form-check-input"
                    v-model="form.type"
                    type="radio"
                    name="user type"
                    v-validate="'required'"
                    value="ADMIN">
                  <span :class="{'font-weight-bold': isAdmin}">Admin</span>
                  <small class="form-text text-muted">
                    Similar to System Admin, but it only applies to his own company.
                  </small>
                </label>
              </div>
              <div class="form-check">
                <label class="form-check-label">
                  <input
                    class="form-check-input"
                    v-model="form.type"
                    v-validate="'required'"
                    type="radio"
                    name="user type"
                    value="DEFAULT">
                  <span :class="{'font-weight-bold': isRegular}">Regular</span>
                  <small class="form-text text-muted">
                    Regular user can only see basic data and manage shipment.
                  </small>
                </label>
              </div>
            </div>
          </div>
          <b-form-feedback>{{ errors.first('user type') }}</b-form-feedback>
          <div
            class="form-row mt-3">
            <div class="col-sm-2">
              <legend>Input Token</legend>
            </div>
            <div class="col-sm-10">
              <input
                name="token"
                type="token"
                class="form-control"
                id="input_token"
                v-model="form.user_token"
                placeholder="Token">
            </div>
          </div>
        </div>
      </div>
      <legend v-if="!isEdit">Company</legend>
      <div
        class="form-row"
        v-if="!isEdit">
        <div class="form-group col-md-6">
          <label>Company</label>
          <company-multi-select
            :multiple="false"
            :having="companyHavingSetting"
            :init-selection-id="userCompanyId"
            @on-selected="onCompanySelected"
            required
          />
        </div>
      </div>
      <legend>Address</legend>
      <div class="form-row">
        <div class="form-group col-md-6">
          <label for="input_address_country">Country</label>
          <input
            name="country"
            type="text"
            v-model="form.address.country"
            class="form-control"
            id="input_address_country"
            placeholder="Country">
        </div>
        <div class="form-group col-md-6">
          <label for="input_address_state">State</label>
          <input
            type="text"
            v-model="form.address.state"
            class="form-control"
            id="input_address_state"
            placeholder="State">
        </div>
      </div>
      <div class="form-row">
        <div class="form-group col-md-6">
          <label for="input_address_city">City</label>
          <input
            type="text"
            v-model="form.address.city"
            class="form-control"
            id="input_address_city"
            placeholder="City">
        </div>
        <div class="form-group col-md-6">
          <label for="input_address_zip_code">Zip / Postal Code</label>
          <input
            type="text"
            v-model="form.address.zip_code"
            class="form-control"
            id="input_address_zip_code"
            placeholder="Zip / Postal Code">
        </div>
      </div>
      <b-form-group
        id="label_address_address1"
        label="Address 1"
        label-for="input_address_address1">
        <b-form-input
          id="input_address_address1"
          type="text"
          v-model="form.address.address1"
          placeholder="Address line 1"/>
      </b-form-group>
      <b-form-group
        id="label_address_address2"
        label="Address 2"
        label-for="input_address_address2">
        <b-form-input
          id="input_address_address2"
          type="text"
          v-model="form.address.address2"
          placeholder="Address line 2"/>
      </b-form-group>
      <hr>
      <b-button
        type="submit"
        id="createBtn"
        :disabled="disabled"
        variant="primary">
        <span v-if="beforeClick">Save changes</span>
        <span v-else> Loading..</span>
        <i v-if="loadingSpinner" class="fa fa-spinner fa-spin"></i>
      </b-button>
      <b-button
        @click="onCancel()"
        type="reset"
        variant="outline-secondary"
        class="pull-right">Cancel</b-button>
    </b-form>
  </b-card>
</template>
<script>
import tmsApi from '@/api/tmsApi';
import { find, isEmpty, isObject, get } from 'lodash';
import { isUndefined } from 'util';

import CompanyMultiSelect from '@/components/CompanyMultiSelect.vue';

export default {
  components: {
    CompanyMultiSelect,
  },
  name: 'user_detail',
  data() {
    return {
      form: {
        id: null,
        full_name: '',
        username: '',
        email: '',
        type: '',
        password: '',
        password2: '',
        company: '',
        address: {
          country: '',
          state: '',
          city: '',
          zip_code: '',
          address1: '',
          address2: '',
        },
        user_token: '',
      },
      loadingSpinner: false,
      beforeClick: true,
      disabled: false,
      usernameHandler: null,
      isDuplicateUsername: false,
    };
  },
  created() {
    const { userId } = this.$route.params;
    const breadcrumb = [
      { to: '', name: 'Home' },
      { to: 'admin', name: 'Admin' },
      { to: 'users', name: 'Users' },
      { to: userId, name: 'Detail' },
      { to: 'edit', name: 'Edit' },
    ];

    if (!this.isEdit) {
      breadcrumb.splice(3, 2);
      breadcrumb.push({ to: 'create', name: 'Create' });
      this.form.company = this.userData.company;
    } else {
      this.$store.commit('users/SET_DETAIL_ID', userId);
      this.$store.dispatch('users/LOAD_DETAIL_DATA');
    }
    this.$store.commit('SET_BREADCRUMB_ITEMS', breadcrumb);
  },
  computed: {
    token() {
      return this.$store.getters.token;
    },
    detailedInfo() {
      return this.$store.getters['users/get_detail_data'];
    },
    userCompanyId() {
      if (this.isEdit) {
        return get(this.detailedInfo, 'company.id', '');
      }
      return this.userData.company_id.toString();
    },
    companyHavingSetting() {
      if (this.userData.type === 'SYSTEM_ADMIN') {
        return '';
      }
      return 'self';
    },
    displayName() {
      return this.detailedInfo.full_name;
    },
    header() {
      const header = this.isEdit
        ? `Edit User: ${this.displayName}`
        : 'Create New User';
      return header;
    },
    userType() {
      const { type } = this.detailedInfo;
      switch (type) {
        case 'SYSTEM_ADMIN':
          return 'System Admin';
        case 'ADMIN':
          return 'Admin';
        default:
          return 'User';
      }
    },
    isSystemAdmin() {
      return this.form.type === 'SYSTEM_ADMIN';
    },
    isAdmin() {
      return this.form.type === 'ADMIN';
    },
    isRegular() {
      return this.form.type === 'DEFAULT';
    },
    isEdit() {
      return !isEmpty(this.$route.params.userId);
    },
    selectedCompany() {
      const { companyId: id } = this.$route.params;
      const selectedCompany = find(this.companies, { id });
      return selectedCompany;
    },
    isDisabled() {
      return this.selectedCompany && true;
    },
    userData() {
      return this.$store.getters.userData;
    },
  },
  watch: {
    detailedInfo(data) {
      const { form } = this;
      const { address } = form;
      form.id = data.id;
      form.full_name = data.full_name;
      form.username = data.username;
      form.email = data.email;
      form.type = data.type;
      form.company = data.company;
      form.user_token = get(data, 'account.user_token', '');
      data.address = data.address || {};
      address.country = data.address.country;
      address.state = data.address.state;
      address.city = data.address.city;
      address.zip_code = data.address.zip_code;
      address.address1 = data.address.address1;
      address.address2 = data.address.address2;
    },
  },
  methods: {
    onUsernameChange(e) {
      if (this.usernameHandler) clearTimeout(this.usernameHandler);
      this.usernameHandler = setTimeout(() => {
        const username = e.target.value;
        if (this.isEdit) {
          if (this.detailedInfo.username === username) return;
        }
        tmsApi.check_username(this.token, { username }).then((resp) => {
          if (isUndefined(resp.error)) {
            this.isDuplicateUsername = !resp.available;
          }
        });
      }, 5e2);
    },
    onCompanySelected(company) {
      if (isObject(company)) {
        this.form.company = company;
      }
    },
    onSubmit() {
      this.validate().then((valid) => {
        if (valid && !this.isDuplicateUsername) {
          this.loadingSpinner = true;
          this.beforeClick = false;
          this.disabled = true;
          let { userId } = this.$route.params;
          const { companyId } = this.$route.params;
          const action = this.isEdit ? 'UPDATE_DATA' : 'CREATE_DATA';

          if (companyId) this.form.company = this.selectedCompany;


          this.$store.dispatch(`users/${action}`, this.form).then((user) => {
            if (companyId) {
              this.$router.push(`/admin/companies/${companyId}/users`);
            } else {
              userId = this.isEdit ? userId : user.id;
              this.$router.push(`/admin/users/${userId}`);
            }
          });
        } else {
          this.loadingSpinner = false;
          this.beforeClick = true;
          this.disabled = false;
        }
      });
    },
    validate() {
      return this.$validator.validateAll();
    },
    onCancel() {
      const { userId, companyId } = this.$route.params;
      if (this.isEdit) {
        this.$router.push(`/admin/users/${userId}`);
      } else if (companyId) {
        this.$router.push(`/admin/companies/${companyId}/employees`);
      } else {
        this.$router.push('/admin/users');
      }
    },
  },
};
</script>
<style lang="sass">
.unavailable
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #f86c6b;
.available
  display: none;
</style>




// WEBPACK FOOTER //
// src/views/pages/admin/users/CreateEdit.vue